/**
 * Tables (Design System §09): header under an ink rule, hairlines between rows, the whole row is the
 * target, the sorted column carries the underline. Empty states are instructions with the action
 * beside them. Skeletons match the real row height and do not animate.
 */
import type { KeyboardEvent } from "react";
import { h, cx, type ReactNode } from "./h.ts";
import { Button } from "./controls.ts";

export interface Column<R> {
  key: string;
  label: ReactNode;
  cell: (row: R, index: number) => ReactNode;
  /** Right-aligned, tabular figures. */
  num?: boolean;
  width?: string;
  sorted?: boolean;
  foot?: () => ReactNode;
}

export interface TableProps<R> {
  columns: Column<R>[];
  rows: R[];
  rowKey: (row: R, index: number) => string;
  onRow?: (row: R, index: number) => void;
  selected?: number;
  onKey?: (e: KeyboardEvent<HTMLTableRowElement>, row: R, index: number) => void;
  /** "4 of 142 rows · sorted by EBITDA est" — the line under the table. */
  summary?: string;
  keyboardHint?: string;
  caption?: string;
}

export function Table<R>(p: TableProps<R>): ReactNode {
  const hasFoot = p.columns.some((c) => c.foot !== undefined);
  return h("div", null,
    // A region that scrolls sideways has to be reachable by keyboard, or a table wider than the
    // screen is unreadable without a mouse (WCAG 2.1.1; axe `scrollable-region-focusable`). The
    // caption or the summary names it, so the reader is told what they have landed in.
    h("div", { className: "oc-scroll", tabIndex: 0, role: "region", "aria-label": p.caption ?? p.summary ?? "Table" },
      h("table", { className: "oc" },
        p.caption === undefined ? null : h("caption", { className: "oc-caption", style: { textAlign: "left" } }, p.caption),
        h("thead", null, h("tr", null, ...p.columns.map((c) => h("th", {
          key: c.key, className: cx(c.num && "num", c.sorted && "sorted"), style: c.width ? { width: c.width } : undefined, scope: "col",
        }, c.label)))),
        h("tbody", null, ...p.rows.map((r, i) => h("tr", {
          key: p.rowKey(r, i), className: cx(p.onRow && "is-row"), "aria-selected": p.selected === i ? "true" : undefined,
          tabIndex: p.onRow ? 0 : undefined,
          onClick: p.onRow ? () => p.onRow!(r, i) : undefined,
          onKeyDown: (e: KeyboardEvent<HTMLTableRowElement>) => {
            if (e.key === "Enter" && p.onRow) { e.preventDefault(); p.onRow(r, i); }
            p.onKey?.(e, r, i);
          },
        }, ...p.columns.map((c) => h("td", { key: c.key, className: cx(c.num && "num") }, c.cell(r, i)))))),
        hasFoot ? h("tfoot", null, h("tr", null, ...p.columns.map((c) => h("td", { key: c.key, className: cx(c.num && "num") }, c.foot?.() ?? null)))) : null,
      )),
    (p.summary || p.keyboardHint) ? h("div", { className: "oc-table-foot" }, h("span", null, p.summary ?? ""), h("span", { className: "kbd" }, p.keyboardHint ?? "")) : null,
  );
}

export function EmptyState({ title, body, actionLabel, onAction }: { title: string; body: string; actionLabel?: string; onAction?: () => void }): ReactNode {
  return h("div", { className: "oc-empty", role: "status" },
    h("div", { className: "title" }, title),
    h("div", { className: "body" }, body),
    actionLabel && onAction ? h("div", { className: "action" }, h(Button, { label: actionLabel, onClick: onAction })) : null,
  );
}

/** Ground one step darker, no animation, widths stepping down like a real table (Design System §11). */
export function Skeleton({ rows = 4, label = "Loading" }: { rows?: number; label?: string }): ReactNode {
  return h("div", { className: "skel-wrap", role: "status", "aria-label": label },
    ...Array.from({ length: rows }, (_, i) => h("div", { key: i, className: "skel", style: { width: `${70 - i * 7}%` } })));
}

/** Ruled rows for a public page — the "each note covers" list, a figures table, a question list. */
export function Rows({ children, columns, close = false }: { children: ReactNode[]; columns?: string; close?: boolean }): ReactNode {
  return h("div", { className: cx("oc-rows", close && "close"), style: columns ? ({ ["--oc-row-cols" as string]: columns } as Record<string, string>) : undefined }, ...children);
}
export function Row({ children, head = false, total = false, columns }: { children: ReactNode[]; head?: boolean; total?: boolean; columns?: string }): ReactNode {
  return h("div", { className: cx("oc-row", head && "head", total && "total"), style: { gridTemplateColumns: columns ?? "minmax(0,1fr)" } }, ...children);
}

/** Definition rows — label left, text right (Design System "kv"; Legal and Details pages). */
export function KeyValues({ items }: { items: Array<[ReactNode, ReactNode]> }): ReactNode {
  return h("dl", { className: "oc-kv" }, ...items.flatMap(([k, v], i) => [h("dt", { key: `k${i}` }, k), h("dd", { key: `v${i}` }, v)]));
}
